import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useChangeStatusQuery } from "../../Services/settingsAPIs/CommonAPI";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Modal,
  Box,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Loader } from "react-bootstrap-typeahead";
import { useBulkStatusMutation } from "../../Services/ticket/BulkAssign";

interface IProps {
  closeBulkStatusModal: () => void;
  selectedTickets: any;
  refetch: any;
  dispositionId?: any; // Pass disposition ID to fetch statuses
}

const BulkStatusModal: React.FC<IProps> = ({
  closeBulkStatusModal,
  selectedTickets,
  refetch,
  dispositionId,
}) => {
  const [selectedStatus, setSelectedStatus] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isStatusLoading, setIsStatusLoading] = useState<boolean>(false);
  const [statusOptions, setStatusOptions] = useState<any[]>([]);

  const [UpdateBulkStatus] = useBulkStatusMutation();

  // Fetch statuses based on disposition ID
  const {
    data: ticketData,
    isLoading: statusQueryLoading,
    refetch: refetchStatuses,
  } = useChangeStatusQuery(dispositionId, {
    skip: !dispositionId,
  });

  // Format status data similar to TicketListView
  const formatStatusData = (status: any) => {
    if (typeof status.status === "string") {
      // Open and Reopen statuses
      return {
        status_type: status.status_type,
        status: status.status,
      };
    } else {
      // WIP and Closed statuses
      return {
        id: status?.status?.id,
        status_type: status?.status_type,
        status: status?.status?.status,
      };
    }
  };

  useEffect(() => {
    if (ticketData?.all_statuses) {
      const formattedOptions = ticketData.all_statuses.map((status: any) =>
        formatStatusData(status)
      );
      setStatusOptions(formattedOptions);
    }
  }, [ticketData]);

  useEffect(() => {
    if (statusQueryLoading) {
      setIsStatusLoading(true);
    } else {
      setIsStatusLoading(false);
    }
  }, [statusQueryLoading]);

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 120,
      },
    },
  };

  const truncateDescription = (description: string, maxLength: number) => {
    if (description?.length > maxLength) {
      return description?.slice(0, maxLength) + "...";
    }
    return description;
  };

  const changeStatusHandler = async () => {
    if (!selectedStatus) {
      toast.error("Please select a status");
      return;
    }

    setIsLoading(true);

    try {
      const payload: any = {
        ticket_ids: selectedTickets,
        status_type: selectedStatus.status_type,
        ...(selectedStatus.status_type === "wip" && {
          status: selectedStatus.id,
        }),
        ...(selectedStatus.status_type === "closed" && {
          closed_status: selectedStatus.id,
        }),
      };

      const response: any = await UpdateBulkStatus({
        body: payload,
        selectedTickets,
      });

      // ✅ RTK Query error handling
      if (response?.error) {
        const apiError =
          response?.error?.data?.detail?.[0] ||
          response?.error?.data?.error ||
          "Failed to update status";
        throw new Error(apiError);
      }

      const data = response?.data;

      // ✅ Handle partial failures
      if (data?.failures?.length) {
        toast.warning(data?.message || "Some tickets failed to update");

        return;
      }

      // ✅ Success
      toast.success(data?.message || "Ticket status successfully updated");
    } catch (err: any) {
      toast.error(err?.message || "Failed to update status");
    } finally {
      setIsLoading(false);

      // ✅ These will ALWAYS run (success / error / partial failure)
      refetch();
      closeBulkStatusModal();
    }
  };

  const extractErrorMessage = (rawError: string) => {
    try {
      const match = rawError.match(/string='([^']+)'/);
      return match ? match[1] : rawError;
    } catch {
      return rawError;
    }
  };

  return (
    <Modal
      open={true}
      onClose={(event, reason) => {
        if (reason && reason === "backdropClick") return;
        closeBulkStatusModal();
      }}
      aria-labelledby="change-status-modal-title"
      aria-describedby="change-status-modal-description"
    >
      <Box
        sx={{
          padding: "1rem",
          backgroundColor: "white",
          margin: "auto",
          width: "400px",
          height: "250px",
          borderRadius: "8px",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          position: "absolute",
          boxShadow: 24,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h5>Change Status</h5>
          <Button
            onClick={closeBulkStatusModal}
            style={{ minWidth: "auto", padding: "0.5rem" }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>

        <div>
          <div className="change-status-section">
            <div className="change-status-section--body mt-4">
              <InputLabel>Status</InputLabel>
              {isStatusLoading ? (
                <div className="mt-4">
                  <Loader />
                </div>
              ) : (
                <FormControl
                  variant="standard"
                  className="full-width w-100 mt-4"
                >
                  <Select
                    value={selectedStatus?.status || ""}
                    onChange={(event) => {
                      const selected = statusOptions.find(
                        (opt) => opt.status === event.target.value
                      );
                      setSelectedStatus(selected);
                    }}
                    displayEmpty
                    style={{ paddingLeft: "1rem" }}
                    MenuProps={MenuProps}
                    disabled={!dispositionId || !statusOptions.length}
                  >
                    <MenuItem value="" disabled>
                      Select Status
                    </MenuItem>
                    {statusOptions?.map((status: any, index: number) => (
                      <MenuItem
                        key={status.id || index}
                        value={status.status}
                        data-bs-toggle="tooltip"
                        title={status?.status}
                      >
                        {truncateDescription(status?.status, 25)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </div>
          </div>
        </div>

        <div
          style={{
            position: "absolute",
            bottom: ".6rem",
            left: "50%",
            transform: "translateX(-50%)",
            width: "90%",
          }}
        >
          <Button
            className="btn btn-primary w-100"
            onClick={changeStatusHandler}
            disabled={!selectedStatus || isLoading}
          >
            {isLoading ? (
              <>
                Updating... <Loader />
              </>
            ) : (
              "Update Status"
            )}
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export default BulkStatusModal;